import supabase from '../lib/supabase';
import { v4 as uuidv4 } from 'uuid';

class PollService {
  // Create new poll
  async createPoll(question, categories) {
    try {
      const pollId = uuidv4().split('-')[0];
      const hostToken = uuidv4();

      const pollData = {
        id: pollId,
        question: question.trim(),
        categories: categories.map(name => ({
          name: name.trim(),
          points: 0,
          comments: [],
          totalInputs: 0
        })),
        host_token: hostToken,
        rounds: [],
        current_round: null,
        total_participants: 0,
        created_at: new Date().toISOString()
      };

      const { data, error } = await supabase
        .from('polls_sv2024')
        .insert([pollData])
        .select();

      if (error) throw error;

      return { pollId, hostToken };
    } catch (error) {
      console.error('Error creating poll:', error);
      throw new Error('Failed to create poll');
    }
  }

  // Get poll data
  async getPoll(id, hostToken = null) {
    try {
      let query = supabase
        .from('polls_sv2024')
        .select('*')
        .eq('id', id);

      if (hostToken) {
        query = query.eq('host_token', hostToken);
      }

      const { data, error } = await query.single();

      if (error) throw error;

      // Return poll data without sensitive information for participants
      const pollData = {
        id: data.id,
        question: data.question,
        categories: data.categories,
        currentRound: data.current_round,
        totalParticipants: data.total_participants
      };

      return pollData;
    } catch (error) {
      console.error('Error getting poll:', error);
      throw new Error('Poll not found');
    }
  }

  // Start/Stop round
  async manageRound(pollId, hostToken, action) {
    try {
      const { data: poll, error: fetchError } = await supabase
        .from('polls_sv2024')
        .select('*')
        .eq('id', pollId)
        .eq('host_token', hostToken)
        .single();

      if (fetchError) throw fetchError;

      let updates = {};

      if (action === 'start') {
        const round = {
          id: uuidv4().split('-')[0],
          startTime: new Date().toISOString(),
          active: true
        };
        
        const newRounds = [...(poll.rounds || []), round];
        updates = {
          rounds: newRounds,
          current_round: round
        };

        // Clear participant sessions in localStorage
        if (typeof window !== 'undefined') {
          Object.keys(localStorage).forEach(key => {
            if (key.startsWith(`poll-${pollId}-session-`)) {
              localStorage.removeItem(key);
            }
          });
        }
      } else if (action === 'stop') {
        if (poll.current_round) {
          const updatedRound = {
            ...poll.current_round,
            active: false,
            endTime: new Date().toISOString()
          };
          updates = {
            current_round: updatedRound
          };
        }
      }

      const { data, error } = await supabase
        .from('polls_sv2024')
        .update(updates)
        .eq('id', pollId)
        .select();

      if (error) throw error;

      return { success: true };
    } catch (error) {
      console.error('Error managing round:', error);
      throw new Error('Failed to manage round');
    }
  }

  // Add input (point or comment)
  async addInput(pollId, type, category, text = null) {
    try {
      // Get current poll data
      const { data: poll, error: fetchError } = await supabase
        .from('polls_sv2024')
        .select('*')
        .eq('id', pollId)
        .single();

      if (fetchError) throw fetchError;

      if (!poll.current_round || !poll.current_round.active) {
        throw new Error('No active round');
      }

      // Check participant input limit (5 per round)
      const sessionKey = `poll-${pollId}-session-${poll.current_round.id}`;
      const currentInputs = parseInt(localStorage.getItem(sessionKey) || '0');

      if (currentInputs >= 5) {
        throw new Error('Maximum inputs reached for this round');
      }

      // Find and update category
      const categories = [...poll.categories];
      const categoryIndex = categories.findIndex(cat => cat.name === category);
      
      if (categoryIndex === -1) {
        throw new Error('Category not found');
      }

      if (type === 'point') {
        categories[categoryIndex].points += 1;
        categories[categoryIndex].totalInputs += 1;
      } else if (type === 'comment') {
        if (!text || text.trim().length === 0) {
          throw new Error('Comment text required');
        }
        
        const comment = {
          text: text.trim(),
          timestamp: new Date().toISOString(),
          participantId: sessionKey
        };
        
        categories[categoryIndex].comments.push(comment);
        categories[categoryIndex].totalInputs += 1;
      }

      // Update poll in database
      const { data, error } = await supabase
        .from('polls_sv2024')
        .update({ categories })
        .eq('id', pollId)
        .select();

      if (error) throw error;

      // Update local session
      localStorage.setItem(sessionKey, (currentInputs + 1).toString());

      return { success: true };
    } catch (error) {
      console.error('Error adding input:', error);
      throw error;
    }
  }

  // Export poll data
  async exportPoll(pollId, hostToken, format) {
    try {
      const { data: poll, error } = await supabase
        .from('polls_sv2024')
        .select('*')
        .eq('id', pollId)
        .eq('host_token', hostToken)
        .single();

      if (error) throw error;

      if (format === 'csv') {
        const csvData = [];
        
        poll.categories.forEach(category => {
          csvData.push({
            Kategorie: category.name,
            Punkte: category.points,
            'Anzahl Kommentare': category.comments?.length || 0,
            'Gesamt Eingaben': category.totalInputs
          });
          
          category.comments?.forEach(comment => {
            csvData.push({
              Kategorie: category.name,
              Kommentar: comment.text,
              Zeitstempel: new Date(comment.timestamp).toLocaleString()
            });
          });
        });

        // Convert to CSV string
        const headers = Object.keys(csvData[0] || {});
        const csvContent = [
          headers.join(','),
          ...csvData.map(row => 
            headers.map(header => `"${(row[header] || '').toString().replace(/"/g, '""')}"`).join(',')
          )
        ].join('\n');

        return csvContent;
      }

      return null;
    } catch (error) {
      console.error('Error exporting poll:', error);
      throw new Error('Failed to export poll');
    }
  }
}

export default new PollService();